/**
 * A profile photo when there is one, initials when there is not.
 *
 * The initials colour is derived from the user id, so the same person is the
 * same colour on everyone's phone — that consistency is what makes people
 * scannable in a fast-moving channel, and it has to keep holding for whoever
 * has not uploaded a photo.
 */
import { useState } from 'react';

const PALETTE = [
  'bg-rose-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-lime-600',
  'bg-emerald-600',
  'bg-teal-600',
  'bg-sky-600',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-fuchsia-500',
];

function colorFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length]!;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

const SIZES = {
  sm: 'h-7 w-7 text-[10px]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-12 w-12 text-sm',
};

export default function Avatar({
  userId,
  name,
  avatarKey,
  size = 'md',
}: {
  userId: string;
  name: string;
  /** Present when the user has uploaded a photo. The value is opaque — its
   *  only job is to tell us a photo exists and to bust the cache when it
   *  changes, since the key is regenerated on every upload. */
  avatarKey?: string | null;
  size?: keyof typeof SIZES;
}) {
  // If the object has gone missing, fall back to initials rather than showing
  // a broken image icon next to someone's name.
  const [failed, setFailed] = useState(false);

  if (avatarKey && !failed) {
    return (
      <img
        src={`/api/avatars/${userId}`}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className={`${SIZES[size]} shrink-0 rounded-full bg-zinc-100 object-cover`}
      />
    );
  }

  return (
    <div
      className={`${SIZES[size]} ${colorFor(userId)} flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none`}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}
